import React from "react";
import styled, { keyframes } from "styled-components";
import Header from "../../Header/Header";
import Footer from "../../Footer/Footer";
import SubVisual from "../Public/SubVisual";
import { HISTORY_DATA } from "../../../Models/ArrowData";
import { DHKS_INFO } from "../../../Models/CompanyInfoData";

const lineAnim = keyframes`
  from { height: 0; }
  to { height: 100%; }
`;

const reveal = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const History = () => {
  return (
    <>
      <Header />
      <SubVisual title="연혁" subTitle="회사소개" />

      <PageContainer>
        <InnerWrapper>
          <SectionHeader>
            <div className="text-group">
              <h5 className="sub">HISTORY</h5>
              <h2 className="main">{DHKS_INFO.companyKRName} 연혁</h2>
            </div>
          </SectionHeader>

          <TimelineContainer>
            <MainVerticalLine />

            {HISTORY_DATA.map((data, idx) => (
              <TimelineItem key={data.year} delay={idx * 0.2}>
                <YearContent>
                  <div className="sticky-box">
                    <h3 className="year">{data.year}</h3>
                    {/* <h4 className="title">{data.title}</h4> */}
                  </div>
                </YearContent>

                <NodeArea>
                  <div className="node-dot" />
                </NodeArea>

                <EventContent>
                  {/* <p className="summary">{data.desc}</p> */}
                  <ul className="event-list">
                    {data.events.map((ev, i) => (
                      <li key={i}>
                        <span className="date">{ev.date}</span>
                        <span className="text">{ev.content}</span>
                      </li>
                    ))}
                  </ul>
                </EventContent>
              </TimelineItem>
            ))}
          </TimelineContainer>
        </InnerWrapper>
      </PageContainer>

      <Footer />
    </>
  );
};

export default History;

/* ================= CSS 스타일 ================= */

const PageContainer = styled.div`
  width: 100%;
  background-color: #fff;
  padding: 120px 0;

  @media (max-width: 768px) {
    padding: 60px 0; /* 모바일에서는 상하 여백 축소 */
  }
`;

const InnerWrapper = styled.div`
  width: 90%;
  max-width: 1300px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 120px;
  animation: ${reveal} 1s ease-out forwards;

  .num {
    font-size: 1.1rem;
    font-weight: 900;
    color: #00d2ff;
    padding-top: 5px;
    &::after {
      content: "";
      display: block;
      width: 20px;
      height: 2px;
      background: #00d2ff;
      margin-top: 5px;
    }
  }

  .text-group {
    .sub {
      font-size: 0.85rem;
      font-weight: 700;
      letter-spacing: 4px;
      color: #888;
      margin-bottom: 12px;
    }
    .main {
      font-size: 3rem;
      font-weight: 800;
      line-height: 1.25;
      color: #1a1a1a;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    margin-bottom: 60px;

    .text-group .main {
      font-size: 2rem;
      br {
        display: none;
      } /* 모바일에서는 자연스럽게 줄바꿈되도록 br 제거 가능 */
    }
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MainVerticalLine = styled.div`
  position: absolute;
  left: 30%;
  top: 0;
  width: 1px;
  height: 100%;
  background-color: #f0f0f0;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: linear-gradient(to bottom, #00d2ff, #0056b3);
    animation: ${lineAnim} 3.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  @media (max-width: 1024px) {
    left: 20px; /* 모바일: 선을 왼쪽으로 고정 */
  }
`;

const TimelineItem = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 160px;
  opacity: 0;
  animation: ${reveal} 0.8s ease-out ${(props) => props.delay}s forwards;
  position: relative; /* 모바일에서 NodeArea 절대 위치의 기준점이 됨 */

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    margin-bottom: 80px; /* 모바일에서는 섹션 간 간격 축소 */
  }
`;

const YearContent = styled.div`
  flex: 0 0 30%;
  padding-right: 60px;
  text-align: right;

  .sticky-box {
    position: sticky;
    top: 180px;
  }

  .year {
    font-size: 4.5rem;
    font-weight: 900;
    line-height: 1;
    color: #1a1a1a;
    letter-spacing: -3px;
    margin-bottom: 8px;
  }
  .title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #00d2ff;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  @media (max-width: 1024px) {
    flex: none;
    text-align: left;
    padding-right: 0;
    padding-left: 50px; /* 선(20px)과 공간 확보 */
    margin-bottom: 20px;

    .sticky-box {
      position: static; /* 모바일 세로 모드에서는 sticky 불필요 */
    }
    .year {
      font-size: 3rem; /* 폰트 사이즈 조정 */
    }
  }
`;

const NodeArea = styled.div`
  flex: 0 0 1px;
  position: relative;
  z-index: 5;

  .node-dot {
    width: 12px;
    height: 12px;
    background-color: #fff;
    border: 2px solid #00d2ff;
    border-radius: 50%;
    position: absolute;
    top: 22px;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: 0 0 10px rgba(0, 210, 255, 0.2);
  }

  @media (max-width: 1024px) {
    position: absolute;
    left: 20px; /* 모바일 MainVerticalLine 위치와 동일하게 설정 */
    top: 0;
    bottom: 0;

    .node-dot {
      top: 15px; /* 모바일 연도(Year) 텍스트 높이에 맞게 조절 */
    }
  }
`;

const EventContent = styled.div`
  flex: 1;
  padding-left: 80px;
  padding-top: 10px;

  .summary {
    font-size: 1.4rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 35px;
    word-break: keep-all;
    line-height: 1.4;
  }

  .event-list {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    li {
      display: flex;
      gap: 20px;
      font-size: 1.05rem;
      line-height: 1.6;

      .date {
        font-weight: 800;
        color: #00d2ff;
        min-width: 55px;
        font-size: 0.95rem;
        width: 100px;
      }
      .text {
        color: #555;
        font-weight: 500;
        word-break: keep-all;
      }
    }
  }

  @media (max-width: 1024px) {
    padding-left: 50px; /* YearContent와 동일한 시작점 */
    padding-top: 0;

    .summary {
      font-size: 1.2rem;
      margin-bottom: 20px;
    }

    .event-list li {
      flex-direction: column; /* 모바일에서는 날짜와 내용을 위아래로 떨어뜨림 (선택사항) */
      gap: 5px;
      margin-bottom: 15px;

      .date {
        min-width: auto;
      }
    }
  }
`;
